import {Button} from "@zendeskgarden/react-buttons";
import {Checkbox, Field, Hint, Label, Textarea} from "@zendeskgarden/react-forms";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {ReactElement, useContext, useEffect, useMemo, useState} from "react";
import {checkAuthorization, userIsAdmin} from "../../authorization/AuthorizationCheckers";
import {Authorizations} from "../../authorization/Authorizations";
import {AuthContext} from "../../contexts/AuthContext";
import BarLoader from "../bar-loader/BarLoader";
import useCustomDriverNoteModal from "./logic/useCustomDriverNoteModal";
import useOpDriverNoteModal from "./logic/useOpDriverNoteModal";
import useWsDriverNoteModal from './logic/useWsDriverNoteModal';
import * as css from "./styles/driver-note-modal.module.scss";


export type DriverNoteLineType = "worksheet" | "operational" | "custom";

export interface DriverNote {
    lineType: DriverNoteLineType;
    lineId: string;
    lineName: string;
    lineValuesLocked: boolean;
    note: string | null;
    isReadOnly: boolean | null;
    accountName?: string | null;
}

export interface DriverNoteModalProps {
    driverName: string | null;
    driverNote: DriverNote;
    isBusy: boolean;
    onSave: (driverNote: DriverNote) => void;
    onClose: () => void;
}

interface DriverNoteModalDeciderProps {
    lineName: string | null;
    lineId: string | null;
    onSave?: () => void;
    onClose?: () => void;
}

export function WsDriverNoteModal({lineId, lineName, onSave, onClose}: DriverNoteModalDeciderProps): ReactElement {
    const {
        driverNoteData,
        isBusy,
        fetchNote: fetch,
        save,
        reset,
    } = useWsDriverNoteModal({
        onSave: onSave,
    });

    useEffect(() => {
        if (lineId) {
            fetch(lineId);
        } else {
            reset();
        }
    }, [lineId]);

    if (!driverNoteData) {
        return <></>;
    }

    return (
        <DriverNoteModal
            driverName={lineName}
            driverNote={driverNoteData}
            isBusy={isBusy}
            onSave={save}
            onClose={() => {
                reset();
                onClose?.();
            }}
        />
    )
}

export function OpDriverNoteModal({lineId, lineName, onSave, onClose}: DriverNoteModalDeciderProps): ReactElement {
    const {
        driverNoteData,
        isBusy,
        fetchNote: fetch,
        save,
        reset,
    } = useOpDriverNoteModal({
        onSave: onSave,
    });

    useEffect(() => {
        if (lineId) {
            fetch(lineId);
        } else {
            reset();
        }
    }, [lineId]);

    if (!driverNoteData) {
        return <></>;
    }

    return (
        <DriverNoteModal
            driverName={lineName}
            driverNote={driverNoteData}
            isBusy={isBusy}
            onSave={save}
            onClose={() => {
                reset();
                onClose?.();
            }}
        />
    )
}

export function CustomDriverNoteModal({lineId, lineName, onSave, onClose}: DriverNoteModalDeciderProps): ReactElement {
    const {
        driverNoteData,
        isBusy,
        fetchNote: fetch,
        save,
        reset,
    } = useCustomDriverNoteModal({
        onSave: onSave,
    });

    useEffect(() => {
        if (lineId) {
            fetch(lineId);
        } else {
            reset();
        }
    }, [lineId]);

    if (!driverNoteData) {
        return <></>;
    }

    return (
        <DriverNoteModal
            driverName={lineName}
            driverNote={driverNoteData}
            isBusy={isBusy}
            onSave={save}
            onClose={() => {
                reset();
                onClose?.();
            }}
        />
    )
}

function DriverNoteModal({driverNote, driverName, isBusy, onSave, onClose}: DriverNoteModalProps): ReactElement {
    const {user} = useContext(AuthContext);

    const [localNote, setLocalNote] = useState<string>("");
    const [localIsReadOnly, setLocalIsReadOnly] = useState<boolean>(false);

    useEffect(() => {
        setLocalNote(driverNote.note ?? "");
    }, [driverNote.note]);

    useEffect(() => {
        setLocalIsReadOnly(driverNote.isReadOnly ?? false);
    }, [driverNote.isReadOnly]);

    const userCanEdit = useMemo<boolean>(() => {
        if (!user) {
            return false;
        }

        if (userIsAdmin(user)) {
            return true;
        }

        if (driverNote.isReadOnly) {
            return false;
        }

        if (!checkAuthorization(user, Authorizations.ModelingMethodsAdmin) && driverNote.lineValuesLocked) {
            return false;
        }

        return true;
    }, [user, driverNote.lineValuesLocked, driverNote.isReadOnly]);

    return (
        <Modal onClose={onClose} className={css.driverNoteModal}>
            <Header>Update Note for "{driverName}"</Header>
            <Body className={css.modalBody}>
                <BarLoader loading={isBusy} />
                {userCanEdit ?
                    <Field>
                        <Label hidden>Note</Label>
                        <Textarea
                            placeholder="Write anything you would like people to know, regarding this driver."
                            disabled={isBusy}
                            minRows={2}
                            maxRows={12}
                            value={localNote}
                            onChange={evt => setLocalNote(evt.target.value)}
                        />
                    </Field>
                    :
                    <p className={css.readOnlyNoteText}>{localNote}</p>
                }

                {userIsAdmin(user) &&
                    <Field className={css.readOnlyCheckbox}>
                        <Checkbox
                            disabled={isBusy}
                            checked={localIsReadOnly ?? false}
                            onChange={evt => setLocalIsReadOnly(evt.target.checked)}
                        >
                            <Label>Read-Only</Label>
                            <Hint>Only allow other Admin users to edit or delete this note.</Hint>
                        </Checkbox>
                    </Field>
                }
            </Body>
            {userCanEdit &&
                <Footer>
                    <FooterItem>
                        <Button
                            isBasic
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                    </FooterItem>
                    <FooterItem>
                        <Button
                            isPrimary
                            disabled={isBusy}
                            onClick={() => {
                                onSave({
                                    ...driverNote,
                                    note: localNote,
                                    isReadOnly: localIsReadOnly,
                                });
                            }}
                        >
                            {isBusy ? "Working..." : "Save Note"}
                        </Button>
                    </FooterItem>
                </Footer>
            }
            <Close aria-label="Close modal"/>
        </Modal>
    );
}
